import { Fragment, ReactNode, useCallback } from "react";
import { Hyperlink } from "@racoon/ui";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { orgGrpcApi } from "~/shared/api/organization/organization.grpc";
import { usePickupLocationCard } from "~/features/pickup-location-modal/hooks/use-pickup-location-card";
import { useCustomerCardModal } from "~/features/customer-card/hooks/use-customer-card-modal";
import { useUserCard } from "~/pages/dashboard/users/ui/use-user-card";
import { useShowOnDashboard } from "~/shared/hooks/useShowOnDashboard";

type Id = bigint | string | number | null | undefined;

/** A comma-separated list whose entries each link on their own. */
export const linkedList = <E extends { id: Id; name: string }>(
  entities: E[],
  cell: (id: Id, name: string) => ReactNode,
): ReactNode =>
  entities.length
    ? entities.map((entity, index) => (
        <Fragment key={`${entity.id ?? ""}|${entity.name}`}>
          {index > 0 && ", "}
          {cell(entity.id, entity.name)}
        </Fragment>
      ))
    : "—";

/** Report cells that link to an entity's card, opened by id. */
export function useReportHyperlinks() {
  const { activeCompanyId } = useAuthSession();
  const { openLocationById, pickupLocationCard } = usePickupLocationCard();
  const { openSingle: openCustomerCard } = useCustomerCardModal({
    providerId: activeCompanyId ?? undefined,
  });
  const [getOrganization] = orgGrpcApi.useLazyGetOrganizationQuery();
  const { openUserById, userCard } = useUserCard();
  const { showTruck } = useShowOnDashboard();

  // The customer card edits the organization it is given, so it needs the full record.
  const openCustomerById = useCallback(
    async (id: bigint | string | number) => {
      const { data } = await getOrganization({ organizationId: BigInt(id) }, true);
      if (data) openCustomerCard(data);
    },
    [getOrganization, openCustomerCard],
  );

  const cell = useCallback(
    (id: Id, name: string | undefined, open: (id: bigint | string | number) => void): ReactNode => {
      if (!name) return "—";
      if (!id || String(id) === "0") return name;
      return <Hyperlink onOpen={() => open(id)}>{name}</Hyperlink>;
    },
    [],
  );

  const locationCell = useCallback(
    (id: Id, name?: string) => cell(id, name, openLocationById),
    [cell, openLocationById],
  );
  const customerCell = useCallback(
    (id: Id, name?: string) => cell(id, name, openCustomerById),
    [cell, openCustomerById],
  );
  const driverCell = useCallback(
    (id: Id, name?: string) => cell(id, name, openUserById),
    [cell, openUserById],
  );
  const plateCell = useCallback(
    (id: Id, plate?: string) => cell(id, plate, showTruck),
    [cell, showTruck],
  );

  return {
    locationCell,
    customerCell,
    driverCell,
    plateCell,
    /** Rendered once per report; the customer card opens through the modal provider. */
    entityCards: (
      <>
        {pickupLocationCard}
        {userCard}
      </>
    ),
  };
}
